import React, { CSSProperties, useMemo } from 'react';

interface HighlightedTextProps {
  text: string;
  searchTerm: string;
  className?: string;
  style?: CSSProperties;
  isSelected?: boolean;
  highlightColor?: string;
  selectedHighlightColor?: string;
}

const DEFAULT_HIGHLIGHT_COLOR = '#E6F4FF';

const BASE_HIGHLIGHT_STYLES: CSSProperties = {
  fontWeight: 700,
  color: '#14151C',
  padding: '0 2px',
  borderRadius: '2px'
};

const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  searchTerm,
  className = '',
  style = {},
  isSelected = false,
  highlightColor = DEFAULT_HIGHLIGHT_COLOR,
  selectedHighlightColor = highlightColor
}) => {
  const content = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return text;
    }

    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase().trim();
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let currentIndex = lowerText.indexOf(lowerSearch);

    if (currentIndex === -1) {
      return text;
    }

    while (currentIndex !== -1) {
      if (currentIndex > lastIndex) {
        parts.push(text.substring(lastIndex, currentIndex));
      }

      const match = text.substring(
        currentIndex,
        currentIndex + lowerSearch.length
      );
      const activeColor = isSelected ? selectedHighlightColor : highlightColor;
      parts.push(
        <span
          key={currentIndex}
          style={{ ...BASE_HIGHLIGHT_STYLES, backgroundColor: activeColor }}
        >
          {match}
        </span>
      );

      lastIndex = currentIndex + lowerSearch.length;
      currentIndex = lowerText.indexOf(lowerSearch, lastIndex);
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return <>{parts}</>;
  }, [text, searchTerm, isSelected, highlightColor, selectedHighlightColor]);

  return (
    <span className={className} style={style}>
      {content}
    </span>
  );
};

export default HighlightedText;
